import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import { getApiOriginOverride } from '@utils/apiRegion';

// Development API URL management
let devCustomApiUrl: string | null = null;

const normalizeHost = (host?: string | null): string | undefined => {
	if (!host) return undefined;
	let normalized = host.trim();
	// Expo env files sometimes include quoted values (e.g. 'https://api...')
	if (
		(normalized.startsWith("'") && normalized.endsWith("'")) ||
		(normalized.startsWith('"') && normalized.endsWith('"'))
	) {
		normalized = normalized.slice(1, -1).trim();
	}
	// Normalize trailing slash to keep URL joins consistent.
	normalized = normalized.replace(/\/+$/, '');
	return normalized || undefined;
};

/**
 * Join origin with a path that starts with `/api/...`.
 * If `host` already ends with `/api`, strip the duplicate so fetch does not hit `/api/api/...`.
 */
export const joinHostWithApiPath = (
	host: string | undefined | null,
	apiAbsolutePath: string
): string => {
	const h = normalizeHost(host) ?? '';
	let path = apiAbsolutePath.startsWith('/')
		? apiAbsolutePath
		: `/${apiAbsolutePath}`;
	if (!h) return path;
	const base = h.replace(/\/+$/, '');
	if (/\/api$/i.test(base)) {
		path = path.replace(/^\/api(\/|$)/, '/');
	}
	return `${base}${path}`;
};

/** Metro / dev-server hostname (same machine as local API when using LAN). */
const parseExpoPackagerHost = (): string | null => {
	const uri = Constants.expoConfig?.hostUri;
	if (!uri || typeof uri !== 'string') return null;
	const cleaned = uri.replace(/^https?:\/\//i, '').trim();
	const host = cleaned.split(':')[0]?.trim();
	return host || null;
};

const isPrivateLanOrLoopbackHost = (host: string): boolean => {
	if (
		host === 'localhost' ||
		host === '127.0.0.1' ||
		host === '0.0.0.0' ||
		host === '[::1]'
	) {
		return true;
	}
	return /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(host);
};

const isTunnelDevHost = (host: string): boolean => {
	const h = host.toLowerCase();
	return (
		h.includes('exp.direct') ||
		h.includes('.expo.dev') ||
		h.includes('ngrok') ||
		h.includes('tunnel')
	);
};

/**
 * Simulator/emulator: React Native + iOS often fails TLS to local HTTPS (mkcert) while Postman works.
 * Use plain HTTP with backend `npm run dev:http` (SMARTSAAS_DEV_HTTP).
 * Set EXPO_PUBLIC_FORCE_LOCAL_HTTPS=1 to keep https://localhost and run normal `npm run dev`.
 */
const nativeSimEmulatorUsesPlainHttp = (): boolean =>
	__DEV__ &&
	Platform.OS !== 'web' &&
	!Device.isDevice &&
	process.env.EXPO_PUBLIC_FORCE_LOCAL_HTTPS !== '1';

/**
 * Physical device on LAN: default API origin is https://<metro-host>:4000.
 * If the backend runs `npm run dev:http`, you must use http — set EXPO_PUBLIC_DEV_PLAIN_HTTP=1
 * (and do not set EXPO_PUBLIC_FORCE_LOCAL_HTTPS).
 */
const useDevPlainHttpForLanDevice = (): boolean =>
	__DEV__ &&
	Platform.OS !== 'web' &&
	Device.isDevice &&
	process.env.EXPO_PUBLIC_FORCE_LOCAL_HTTPS !== '1' &&
	(process.env.EXPO_PUBLIC_DEV_PLAIN_HTTP === '1' ||
		process.env.EXPO_PUBLIC_DEV_PLAIN_HTTP === 'true');

/**
 * Native __DEV__ only: full API origin when `EXPO_PUBLIC_API_URL` is unset (scheme + host + port).
 * Set to '' to use Metro host / loopback discovery instead.
 */
const NATIVE_DEV_API_ORIGIN_OVERRIDE = '';

/**
 * Native __DEV__ API origin when `EXPO_PUBLIC_API_URL` is unset.
 * Simulator/emulator: loopback on the host (Metro hostUri is often a LAN IP — wrong for simulators).
 * Physical device: same host as Metro (LAN) so the phone can reach the dev machine.
 */
const resolveNativeDevApiOrigin = (): string => {
	const override = normalizeHost(NATIVE_DEV_API_ORIGIN_OVERRIDE);
	if (override) {
		return override;
	}
	if (!Device.isDevice) {
		// const https = process.env.EXPO_PUBLIC_FORCE_LOCAL_HTTPS === '1';
		const https = false;
		if (Platform.OS === 'android') {
			return https ? 'https://10.0.2.2:4000' : 'http://10.0.2.2:4000';
		}
		return https ? 'https://localhost:4000' : 'http://localhost:4000';
	}

	const packagerHost = parseExpoPackagerHost();
	if (
		packagerHost &&
		isPrivateLanOrLoopbackHost(packagerHost) &&
		!isTunnelDevHost(packagerHost)
	) {
		if (
			packagerHost === 'localhost' ||
			packagerHost === '127.0.0.1' ||
			packagerHost === '[::1]'
		) {
			return Platform.OS === 'android'
				? 'https://10.0.2.2:4000'
				: 'https://localhost:4000';
		}
		const scheme = useDevPlainHttpForLanDevice() ? 'http' : 'https';
		return `${scheme}://${packagerHost}:4000`;
	}
	return Platform.OS === 'android'
		? 'https://10.0.2.2:4000'
		: 'https://localhost:4000';
};

/**
 * iOS Simulator: TLS + RN often fail against https://127.0.0.1 even when Postman works; use localhost.
 * Android emulator: 127.0.0.1/localhost point at the emulator — use 10.0.2.2 for the host machine.
 * Applies to saved dev URL / env so AsyncStorage EXPO_PUBLIC_API_URL=127.0.0.1 still works.
 */
const rewriteLoopbackOriginForNativeSimulator = (origin: string): string => {
	if (!__DEV__ || Platform.OS === 'web' || !origin?.trim()) {
		return origin;
	}
	try {
		const u = new URL(origin.trim());
		// iOS __DEV__: always map numeric loopback → localhost (RN TLS + 127.0.0.1 is flaky; some builds misreport Device.isDevice).
		if (Platform.OS === 'ios') {
			if (u.hostname === '127.0.0.1' || u.hostname === '::1') {
				u.hostname = 'localhost';
				return normalizeHost(u.origin) ?? u.origin;
			}
		}
		// Android emulator only — physical device needs real LAN IP.
		if (Platform.OS === 'android' && !Device.isDevice) {
			if (
				u.hostname === '127.0.0.1' ||
				u.hostname === 'localhost' ||
				u.hostname === '::1'
			) {
				u.hostname = '10.0.2.2';
				return normalizeHost(u.origin) ?? u.origin;
			}
		}
	} catch {
		return origin.replace(/\/+$/, '');
	}
	return origin.replace(/\/+$/, '');
};

/** Downgrade https://localhost (etc.) to http for simulators when using plain-HTTP backend dev mode. */
const coerceHttpsToHttpForNativeSimulator = (origin: string): string => {
	if (!nativeSimEmulatorUsesPlainHttp() || !origin?.trim()) {
		return origin;
	}
	try {
		const u = new URL(origin.trim());
		if (u.protocol !== 'https:') {
			return origin.replace(/\/+$/, '');
		}
		const h = u.hostname;
		const isIosLocal =
			Platform.OS === 'ios' &&
			(h === 'localhost' || h === '127.0.0.1' || h === '::1');
		const isAndroidEmuLocal =
			Platform.OS === 'android' &&
			!Device.isDevice &&
			(h === '10.0.2.2' ||
				h === 'localhost' ||
				h === '127.0.0.1' ||
				h === '::1');
		if (isIosLocal || isAndroidEmuLocal) {
			u.protocol = 'http:';
			return (normalizeHost(u.origin) ?? u.origin).replace(/\/+$/, '');
		}
	} catch {
		/* keep origin */
	}
	return origin.replace(/\/+$/, '');
};

/** When EXPO_PUBLIC_DEV_PLAIN_HTTP=1, downgrade https://192.168.x.x (etc.) to http for saved/env URLs. */
const coerceHttpsToHttpForDevPlainLan = (origin: string): string => {
	if (!useDevPlainHttpForLanDevice() || !origin?.trim()) {
		return origin;
	}
	try {
		const u = new URL(origin.trim());
		if (u.protocol !== 'https:') {
			return origin.replace(/\/+$/, '');
		}
		const h = u.hostname;
		if (
			isPrivateLanOrLoopbackHost(h) &&
			!isTunnelDevHost(h) &&
			h !== 'localhost'
		) {
			u.protocol = 'http:';
			return (normalizeHost(u.origin) ?? u.origin).replace(/\/+$/, '');
		}
	} catch {
		/* keep origin */
	}
	return origin.replace(/\/+$/, '');
};

const finalizeNativeDevApiHost = (host: string | undefined): string => {
	const h = normalizeHost(host) ?? '';
	if (!h) return '';
	const lanCoerced = coerceHttpsToHttpForDevPlainLan(h);
	const coerced = coerceHttpsToHttpForNativeSimulator(lanCoerced);
	return rewriteLoopbackOriginForNativeSimulator(coerced);
};

// Function to get the current API host
export const getApiHost = async (): Promise<string> => {
	// In development, check for custom API URL
	if (__DEV__) {
		try {
			const savedUrl = await AsyncStorage.getItem('dev_custom_api_url');
			if (savedUrl && savedUrl.trim()) {
				const resolved = normalizeHost(savedUrl) ?? savedUrl;
				const out = finalizeNativeDevApiHost(resolved);
				if (out !== resolved) {
					console.log('Rewrote dev API URL for simulator:', resolved, '->', out);
				} else {
					console.log('Using custom API URL:', resolved);
				}
				return out;
			}
		} catch (error) {
			console.warn('Failed to load custom API URL:', error);
		}
	}

	// Default host configuration (native dev: env, else Metro host for LAN devices, else loopback)
	const rawHost =
		Platform.OS != 'web'
			? __DEV__
				? normalizeHost(process.env.EXPO_PUBLIC_API_URL) ?? resolveNativeDevApiOrigin()
				: getApiOriginOverride() ?? normalizeHost(process.env.EXPO_PUBLIC_API_URL) ?? process.env.EXPO_PUBLIC_API_URL
			: getApiOriginOverride() ?? process.env.EXPO_PUBLIC_WEB_API;

	const normalizedHost = normalizeHost(rawHost);

	// Web fallback: if env is missing/misconfigured, try same-origin.
	if (!normalizedHost && Platform.OS === 'web' && typeof window !== 'undefined') {
		return window.location.origin;
	}

	// If still missing, return empty string to avoid "undefined/..." URLs.
	if (!normalizedHost) return '';
	return Platform.OS !== 'web' && __DEV__
		? finalizeNativeDevApiHost(normalizedHost)
		: normalizedHost;
};

// Legacy HOST export for backward compatibility
export const HOST =
	Platform.OS != 'web'
		? __DEV__
			? finalizeNativeDevApiHost(
					normalizeHost(process.env.EXPO_PUBLIC_API_URL) ?? resolveNativeDevApiOrigin()
				)
			: getApiOriginOverride() ?? normalizeHost(process.env.EXPO_PUBLIC_API_URL) ?? process.env.EXPO_PUBLIC_API_URL ?? ''
		: getApiOriginOverride() ?? process.env.EXPO_PUBLIC_WEB_API;

/**
 * smartsaas-ml endpoints, reached through the backend proxy (the ML service only
 * accepts the backend's service token). Requests must send the session cookie:
 * use `credentials: 'include'`.
 */
export const AI_API_URL = `${HOST ?? ''}/api/ai/ml`;
export const AI_CHAT_API_URL = `${process.env.EXPO_PUBLIC_AI_CHAT_API}`;
export const AI_MODEL = `${process.env.EXPO_PUBLIC_AI_MODEL}`;

// Dynamic API base URL that respects development custom URLs
export const getApiBaseUrl = async (): Promise<string> => {
	const host = await getApiHost();
	return `${host}/api`;
};

// Legacy export for backward compatibility
export const USER_API_BASE_URL = `${HOST}/api`;

// Function to update all axios instances with new base URL
export const updateApiBaseUrls = async (newHost?: string) => {
	const host = newHost || (await getApiHost());
	const baseUrl = `${host}/api`;

	// Update the global API instances
	if (typeof window !== 'undefined' && (window as any).apiInstances) {
		const instances = (window as any).apiInstances;
		if (instances.api) instances.api.defaults.baseURL = baseUrl;
		if (instances.noCreds) instances.noCreds.defaults.baseURL = baseUrl;
		if (instances.publicApi) instances.publicApi.defaults.baseURL = baseUrl;
		if (instances.sessionApi)
			instances.sessionApi.defaults.baseURL = `${host}/api/session`;
	}

	console.log('Updated API base URLs to:', baseUrl);
	return baseUrl;
};

export const STRIPE_URI = `${HOST}/stripe`;

export const LOGIN_API_URL = `${USER_API_BASE_URL}/login`;

export const PCO_SYSTEM_API_URL = `${USER_API_BASE_URL}/systems`;

export const USER_ACTION_HISTORY = `${USER_API_BASE_URL}/userActionHistory`;

export const AVATAR_UPLOAD = `${USER_API_BASE_URL}/upload/`;

export const GET_AVATAR_URL = `${USER_API_BASE_URL}/image`;

export const NEW_USER_ADDED = `${USER_API_BASE_URL}/registeruser`;

export const SESSION_API = `${USER_API_BASE_URL}/session`;

export const WEATHER_API_KEY = `26d18df2b88c8184fbc6d80c0ae2da66`;

export const NEWS_API_KEY = `GfdrfLQWB5WAkb6DaYEnWT6Or9Zx62547BrDl3_XBh4`;

export const LINKEDIN_CLIENT_ID = process.env.EXPO_PUBLIC_LINKEDIN_CLIENTID || '';

export const COMPANIESHOUSE_API_KEY = '4d57ab9a-b311-4f16-b71b-a811ced9e95c';
